import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BasicModal, {
  BasicModalProps,
} from "@components/core/modals/BasicModal";

import { OptionType } from "@/type/core/form/field.types";
import { CanvasFileResult } from "@/type/core/image/image.types";
import {
  OFFERED_SIGNATURE_TEXT_FONTS,
  TEXT_PICTURE_CANVAS_PREVIEW_HEIGHT,
  TEXT_PICTURE_CANVAS_PREVIEW_WIDTH,
} from "@/utils/Constant";
import { canvasToBlob } from "@/utils/helpers/canvas-helper";
import InputFormField from "@components/core/inputs/InputFormField";
import RadioFormField from "@components/core/inputs/RadioFormField";

const FONT_SIZE = 36;
const FILE_NAME = "imageDeSignatureEnText.png";

interface SignatureTextPictureModalProps extends BasicModalProps {
  onSubmit: (file: File) => void;
}

const SignatureTextPictureModal: FunctionComponent<
  SignatureTextPictureModalProps
> = ({ onSubmit, ...props }) => {
  const { t } = useTranslation();

  const [text, setText] = useState<string>("");
  const [selectedFont, setSelectedFont] = useState<string>(
    OFFERED_SIGNATURE_TEXT_FONTS[0],
  );
  const [image, setImage] = useState<CanvasFileResult>();

  useEffect(() => {
    const getImage = (): Promise<CanvasFileResult> => {
      return new Promise((resolve, reject) => {
        const canvas = document.createElement("canvas");
        canvas.width = TEXT_PICTURE_CANVAS_PREVIEW_WIDTH;
        canvas.height = TEXT_PICTURE_CANVAS_PREVIEW_HEIGHT;
        const x = canvas.width / 2;
        const y = canvas.height / 2;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject("Impossible de charger le context");
          // eslint-disable-next-line quotes
          console.error('Can\'t load canvas.getContext("2d")');
          return;
        }
        let fontSize = FONT_SIZE;
        ctx.font = `${fontSize}px ${selectedFont}`;
        while (ctx.measureText(text).width > canvas.width) {
          fontSize -= 2;
          if (fontSize < 12) {
            reject();
          }
          ctx.font = `${fontSize}px ${selectedFont}`;
        }
        ctx.textAlign = "center";
        ctx.fillText(text, x, (y * 5) / 4);
        canvasToBlob(canvas, FILE_NAME)
          .then((canvasFile) => {
            resolve(canvasFile);
          })
          .catch((error: string) => {
            reject(new Error(error));
          });
      });
    };

    if (text !== "") {
      getImage()
        .then((image) => {
          setImage(image);
        })
        .catch(() => null);
    }
  }, [text, selectedFont]);

  return (
    <BasicModal
      {...props}
      title={t("modals.info.signatureTextPicture.title")}
      onOk={() => {
        if (image?.file) {
          onSubmit(image.file);
          props.onCancel();
        }
      }}
      buttonText={t("modal.save")}
    >
      <div className="signature-text-picture-modal">
        <span className="description">
          {t("modals.info.signatureTextPicture.body")}
        </span>
        <InputFormField
          className="input"
          module="modals.info.signatureTextPicture"
          field="input"
          onChange={(ev) => {
            ev.preventDefault();
            setText(ev.target.value);
          }}
          maxLength={30}
        />
        {text && text.length > 0 && (
          <RadioFormField
            module="modals.info.signatureTextPicure"
            field="radio"
            value={selectedFont}
            onChange={(ev) => {
              ev.preventDefault();
              setSelectedFont(ev.target.value as string);
            }}
            options={OFFERED_SIGNATURE_TEXT_FONTS.map(
              (offeredSignature): OptionType => ({
                label: text,
                value: offeredSignature,
                className: offeredSignature,
              }),
            )}
          />
        )}
        <span className="preview">
          {t("modals.info.signatureTextPicture.preview")} :
        </span>
        <img
          src={image?.url}
          width={TEXT_PICTURE_CANVAS_PREVIEW_WIDTH}
          height={TEXT_PICTURE_CANVAS_PREVIEW_HEIGHT}
          className={"image"}
        />
      </div>
    </BasicModal>
  );
};

export default SignatureTextPictureModal;
